import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	ChartOptions,
	ChartType,
	LineElement,
	LinearScale,
	PluginOptionsByType,
	Tooltip,
	TooltipPositionerFunction,
} from "chart.js";
import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";

import { useTruncatedText } from "../../../../../../../../../components/Text/TruncatedText/internal/useTruncatedText";
import PyTooltip from "../../../../../../../../../components/Tooltip";
import { drawBellCurvePlugin, drawTopBorderPlugin, getBarChartPlugins, getColumnChartPlugins } from "./BarChartPlugins";

import { getColor } from "../../../../../../../../../utils/getColor";

import css from "../../Layout/Layout.module.scss";
import { avatarCache } from "../../shared/avatarCache";
import { Label } from "../UseChartOptions/useChartOptions";

ChartJS.defaults.font.family = "Source Sans Pro, sans-serif";

type BarTooltip = Partial<Omit<PluginOptionsByType<"bar">["tooltip"], "callbacks">> & {
	callbacks: Partial<PluginOptionsByType<"bar">["tooltip"]["callbacks"]>;
};
export interface BarChartProps {
	horizontal?: boolean;
	labels?: Label[];
	datasets?: number[];
	tooltip?: BarTooltip;
	xLabelText?: string;
	yLabelText?: string;
	gaussianDataset?: number[] | boolean;
	isCountAggregation?: boolean;
	showBellCurve?: boolean;
}

interface BarChartOptions extends ChartOptions<"bar"> {
	barThickness: number;
}

declare module "chart.js" {
	interface TooltipPositionerMap {
		horizontalBarTooltipPositioner: TooltipPositionerFunction<ChartType>;
	}
	interface BarElement {
		height: number;
		width: number;
	}
}

const bellCurveImage = new Image();
bellCurveImage.src = "/static/img/charts/BellCurve.png";

// A bar chart that handles avatar images, horizontal, vertical bar and tooltips
const BarChart = React.forwardRef<any, BarChartProps>((props, ref: any) => {
	const { labels, datasets, gaussianDataset, showBellCurve, horizontal, xLabelText, yLabelText, isCountAggregation } =
		props;
	const [canvasWidth, setCanvasWidth] = useState(0);
	const [canvasHeight, setCanvasHeight] = useState(0);
	const isHistogram = !!gaussianDataset; // If there is a gaussian dataset, it is a histogram

	const barTooltip: BarTooltip = {
		displayColors: false,
		enabled: true,
		backgroundColor: getColor("neutral", 7),
		callbacks: {
			title: function (context) {
				const labelData = labels?.[context[0].dataIndex];
				const prefix = isHistogram ? "Bin " : "";
				return labelData?.name ? prefix + labelData.name : "-";
			},
			label: function (context) {
				const labelData = context.raw as number;

				if (isHistogram) {
					const peak = datasets ? Math.max(...datasets) : 0;
					const fakeZeroBarValue = (peak * 0.01) / 2;
					return labelData ? yLabelText + `: ${labelData === fakeZeroBarValue ? 0 : labelData}` : "0";
				}

				if (isCountAggregation) {
					const formCount = labelData ? labelData.toString() : "0";
					const formLabel = labelData === 1 ? "Form" : "Forms";
					return formCount + " " + formLabel;
				}
				return labelData ? labelData.toFixed(2) : "0";
			},
		},
	};

	const { didTruncateText: didTruncateXLabel } = useTruncatedText(
		xLabelText || "",
		canvasWidth * 0.7 || 500,
		"paragraph-2",
		"regular"
	);
	const { didTruncateText: didTruncateYLabel } = useTruncatedText(
		yLabelText || "",
		canvasHeight * 0.7 || 500,
		"paragraph-2",
		"regular"
	);

	useEffect(() => {
		// Register Chart.js components
		if (horizontal) {
			Tooltip.positioners.horizontalBarTooltipPositioner = function (elements) {
				if (!elements.length) {
					return false;
				}

				const firstElement = elements[0];
				const element = firstElement.element;

				// Use type assertion to access properties
				const barElement = element as BarElement;

				let x = barElement.x;
				let y = barElement.y;

				y += barElement.height / 2;
				x -= barElement.width / 2;
				return { x: x, y: y, xAlign: "center", yAlign: "top" };
			};
		}

		// Register Chart.js components

		ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Tooltip);

		if (ref && ref.current) {
			const chartCanvas = ref.current.canvas;
			const ctx = chartCanvas.getContext("2d");
			const dpr = 2;
			const rect = chartCanvas.getBoundingClientRect();
			chartCanvas.width = rect.width * dpr;
			chartCanvas.height = rect.height * dpr;
			ctx.scale(dpr, dpr);
			ref.current.update();
		}
	}, []);

	useEffect(() => {
		// Observe size changes
		const resizeObserver = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;

			// Delay the update to fix the issue where the canvas width are not updated
			requestAnimationFrame(() => {
				setCanvasWidth(width);
				setCanvasHeight(height);
				ref?.current?.update();
			});
		});

		resizeObserver.observe(ref?.current.canvas);

		return () => {
			resizeObserver.disconnect();
		};
	}, [xLabelText, yLabelText, ref?.current]);

	const plugins = useMemo(() => {
		let chartPlugins;

		if (!horizontal) {
			chartPlugins = [getColumnChartPlugins(yLabelText)];
		} else {
			chartPlugins = [getBarChartPlugins(xLabelText, avatarCache)];
		}

		if (isHistogram && showBellCurve) {
			chartPlugins.push(drawBellCurvePlugin);
		}

		if (isHistogram) {
			chartPlugins.push(getBarChartPlugins(xLabelText, avatarCache));
			chartPlugins.push(drawTopBorderPlugin);
		}

		return chartPlugins;
	}, [horizontal, xLabelText, yLabelText, isHistogram, showBellCurve]);

	const chartLabels = labels?.slice() || [];

	if (horizontal) {
		barTooltip.position = "horizontalBarTooltipPositioner";
	}

	const getChartDatasets = () => {
		const chartDatasets: any = [
			{
				data: datasets,
				backgroundColor: getColor("primary", 5),
				barPercentage: 0.83,
				order: 1,
				type: "bar",
			},
		];

		if (gaussianDataset && showBellCurve) {
			chartDatasets.push({
				data: gaussianDataset,
				backgroundColor: getColor("primary", 3),
				barPercentage: 0.83,
				order: 0,
				type: "line",
				tension: 0.4,
				pointRadius: 0,
				borderDash: [15, 15],
			});
		}

		return chartDatasets;
	};

	const chartDatasets = useMemo(() => getChartDatasets(), [datasets, gaussianDataset, showBellCurve]);

	const options: BarChartOptions = {
		indexAxis: horizontal ? "y" : "x",
		responsive: true,
		barThickness: isHistogram ? 75 : 12,
		maintainAspectRatio: false,
		devicePixelRatio: 2,
		elements: {
			bar: {
				borderWidth: 0,
				borderRadius: 2,
			},
		},
		plugins: {
			tooltip: barTooltip,
			legend: {
				display: true,
			},
		},
		scales: {
			x: {
				display: true,
				stacked: true,
				grid: {
					drawTicks: false,
				},
				// this will be drawn in plugins
				title: {
					display: false,
				},
				ticks: {
					callback: function (data) {
						if (horizontal) {
							return data;
						} else {
							return ""; // had custom plugins to handle the label
						}
					},
				},
			},
			y: {
				display: !isHistogram,
				grid: {
					drawTicks: false,
				},
				// this will be drawn in plugins
				title: {
					display: false,
				},
				ticks: {
					callback: function (data) {
						if (horizontal) {
							return "";
						} else {
							return data;
						}
					},
				},
			},
		},
		layout: {
			padding: {
				top: 16,
				left: horizontal ? 130 : 50,
				right: 16,
				bottom: 120,
			},
		},
	};

	return (
		<div className={css.relativeContainer}>
			<Bar
				data-testid="barChart"
				key={
					horizontal
						? "horizontal" + isHistogram + xLabelText + yLabelText
						: "vertical" + xLabelText + yLabelText + isHistogram + showBellCurve
				}
				ref={ref}
				options={options}
				data={{
					labels: chartLabels,
					datasets: chartDatasets,
				}}
				plugins={[...plugins]}
			/>

			{(horizontal || isHistogram) && didTruncateXLabel && (
				<div className={css.horizontalBarTooltip}>
					<PyTooltip message={xLabelText || ""}>
						<div className={css.tooltipTextPlaceholder} style={{ width: canvasWidth * 0.7 }} />
					</PyTooltip>
				</div>
			)}

			{!horizontal && didTruncateYLabel && (
				<div className={css.barTooltip}>
					<PyTooltip message={yLabelText || ""}>
						<div className={css.tooltipVerticalTextPlacholder} style={{ height: canvasHeight * 0.7 }} />
					</PyTooltip>
				</div>
			)}
		</div>
	);
});

BarChart.displayName = "BarChart";

export default React.memo(BarChart);
